import { publicApi as api } from "./publicApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    postOrdersCreate: build.mutation<
      PostOrdersCreateApiResponse,
      PostOrdersCreateApiArg
    >({
      query: (queryArg) => ({
        url: `/orders/create`,
        method: "POST",
        body: queryArg.createOrder
      })
    }),
    getOrdersByOrderId: build.query<
      GetOrdersByOrderIdApiResponse,
      GetOrdersByOrderIdApiArg
    >({
      query: (queryArg) => ({ url: `/orders/${queryArg.orderId}` })
    }),
    postOrdersByOrderIdCancel: build.mutation<
      PostOrdersByOrderIdCancelApiResponse,
      PostOrdersByOrderIdCancelApiArg
    >({
      query: (queryArg) => ({
        url: `/orders/${queryArg.orderId}/cancel`,
        method: "POST"
      })
    }),
    getOrdersByOrderIdFulfillmentStatus: build.query<
      GetOrdersByOrderIdFulfillmentStatusApiResponse,
      GetOrdersByOrderIdFulfillmentStatusApiArg
    >({
      query: (queryArg) => ({
        url: `/orders/${queryArg.orderId}/fulfillment-status`
      })
    }),
    getOrdersByAggregateIdEvents: build.query<
      GetOrdersByAggregateIdEventsApiResponse,
      GetOrdersByAggregateIdEventsApiArg
    >({
      query: (queryArg) => ({
        url: `/orders/${queryArg["aggregate-id"]}/events`,
        params: {
          offset: queryArg.offset,
          fromDateTime: queryArg.fromDateTime,
          limit: queryArg.limit
        }
      })
    }),
    getOrdersEvents: build.query<
      GetOrdersEventsApiResponse,
      GetOrdersEventsApiArg
    >({
      query: (queryArg) => ({
        url: `/orders/events`,
        params: {
          limit: queryArg.limit,
          offset: queryArg.offset,
          fromDateTime: queryArg.fromDateTime
        }
      })
    })
  }),
  overrideExisting: false
});
export { injectedRtkApi as ordersApi };
export type PostOrdersCreateApiResponse =
  /** status 202 Accepted */ CreateOrderResponse;
export type PostOrdersCreateApiArg = {
  createOrder: CreateOrder;
};
export type GetOrdersByOrderIdApiResponse = /** status 200 OK */ Order;
export type GetOrdersByOrderIdApiArg = {
  /** The Order ID generated and supplied by AutoStore */
  orderId: string;
};
export type PostOrdersByOrderIdCancelApiResponse = unknown;
export type PostOrdersByOrderIdCancelApiArg = {
  /** The Order ID generated and supplied by AutoStore */
  orderId: string;
};
export type GetOrdersByOrderIdFulfillmentStatusApiResponse =
  /** status 200 Order fulfillment status retrieved successfully */ OrderFulfillmentState;
export type GetOrdersByOrderIdFulfillmentStatusApiArg = {
  /** The ID of the order to retrieve the fulfillment status for. */
  orderId: string;
};
export type GetOrdersByAggregateIdEventsApiResponse = /** status 200 OK */ {
  /** The current feed’s uri */
  feed_url?: string;
  /** The uri for fetching the next page of the feed */
  next_url?: string | null;
  /** A collection of event objects */
  items?: OrderEvent[];
};
export type GetOrdersByAggregateIdEventsApiArg = {
  /** A unique identifier of an aggregate. For example uuid of PickId, PutAwayTaskId, etc */
  "aggregate-id": string;
  /** Every event will have a sequence number. When providing an offset, only the events containing offset that is equal or greater than provided are returned. */
  offset?: number;
  /** Querying by fromDateTime will yield events since the fromDateTime */
  fromDateTime?: string;
  /** Maximum number of events to include. */
  limit?: number;
};
export type GetOrdersEventsApiResponse = /** status 200 OK */ {
  /** The current feed’s uri */
  feed_url?: string;
  /** The uri for fetching the next page of the feed */
  next_url?: string | null;
  /** A collection of event objects */
  items?: OrderEvent[];
};
export type GetOrdersEventsApiArg = {
  limit?: number;
  offset?: number;
  fromDateTime?: string;
};
export type CreateOrderResponse = {
  /** Unique identifier for the order */
  orderId?: string;
};
export type ProblemDetails = {
  /** A URI reference that identifies the problem type */
  type?: string | null;
  /** A short, human-readable summary of the problem type */
  title: string;
  /** A human-readable explanation specific to this occurrence of the problem */
  detail?: string | null;
  /** A URI reference that identifies the specific occurrence of the problem */
  instance?: string | null;
};
export type Quantity = {
  /** The unit */
  unit: string;
  /** The amount of unit */
  value: number;
};
export type OrderLineTote = {
  /** The Tote identifier */
  id: string;
  /** Layer in which to package the product. Determines priority of picking */
  priority?: number | null;
};
export type NullableWeightThreshold = {
  /** Minimum weight threshold. */
  minimum: Quantity;
  /** Maximum weight threshold. */
  maximum: Quantity;
} | null;
export type WeightThreshold = {
  /** Minimum weight threshold. */
  minimum: Quantity;
  /** Maximum weight threshold. */
  maximum: Quantity;
};
export type WeightThresholdRules = {
  warn?: NullableWeightThreshold;
  error: WeightThreshold;
};
export type FulfillmentRequirements = {
  weightThresholds?: WeightThresholdRules;
} | null;
export type OrderLineAcceptableSubstitutions = {
  sku: string;
  quantity: Quantity;
  fulfillmentRequirements?: FulfillmentRequirements;
};
export type OrderLine = {
  /** SKU identifier for this product */
  sku: string;
  /** Identifier for the order line. This is client facing */
  externalLineItemId: string;
  /** Customer notes on the order line */
  instructions?: string | null;
  quantity: Quantity;
  tote?: OrderLineTote;
  /** List of acceptable substitutions for this line item */
  acceptableSubstitutions?: OrderLineAcceptableSubstitutions[] | null;
  fulfillmentRequirements?: FulfillmentRequirements;
};
export type CreateOrder = {
  /** AutoStore's primary identifier for the order. If an identifier is sent, matches an existing order, and that order has not been batched, the order will be updated with the data supplied in the request. */
  id?: string | null;
  /** Client system's primary identifier for the order. Identifiers cannot be reused. If the identifier sent matches an existing order and that order has not been batched, the order will be updated with the data supplied in the request. */
  externalOrderId: string;
  /** AutoStore's primary identifier for the fulfillment center where the order should be fulfilled. */
  fulfillmentCenterId: string;
  /** Label to distinguish the order types. Used during batching to group orders or time-box batching. */
  orderType: "Fresh" | "Shop";
  /** Express orders will be prioritized above standard orders during batching and picking. Defaults to Standard. */
  priority?: ("Standard" | "Express") | null;
  /** The date/time with relative UTC offset representing when the order was created. */
  createdDate?: string | null;
  /** First and last name of the customer, for displaying to Ops users */
  customerName?: string | null;
  /** This property is obsolete and no longer in use. */
  deliveryChannel?: string | null;
  /** The date/time with relative UTC offset representing when all picking should be complete for the order. Used for prioritization during batching and picking. */
  pickingEndTime: string;
  /** The date/time with relative UTC offset representing when picking can begin. Must be before the Picking End Time. */
  earliestPickingStartTime?: string | null;
  /** When the customer should receive the order. Note that the time zone offsets (-04:00 in this example) must match the FC time zone offset, including across Daylight Saving Time shifts. This field is deprecated. Please use PickingEndTime. */
  orderWindow?: string[] | null;
  /** Indicates whether or not the customer has enabled or disabled substitutions on this order. Use 'ForbidSubstitutes' or leave empty. */
  substitutionPreference?: (null | "ForbidSubstitutes") | null;
  /** Text field the customer entered with any notes */
  specialRequest?: string | null;
  /** List of line items */
  lineItems: OrderLine[];
};
export type Order = {
  /** Primary identifier for the order. This is the identifier used internally to AutoStore. This can be optionally provided at request time. Otherwise an orderId will be created. */
  id?: string;
  /** Order identifier from the client ecommerce system */
  externalOrderId?: string;
  /** Identifier of the MFC */
  fulfillmentCenterId?: string;
  /** label to distinguish the order types. */
  orderType?: string;
  /** Express orders will be prioritized above standard orders during waving and batching. This is relevant for picking, as express orders will be made available for picking sooner. */
  priority?: string;
  /** When the order was created. Note that the time zone offset (-04:00 in this example) must match the FC time zone offset, including across Daylight Saving Time shifts */
  createdDate?: string;
  /** First and last name of the customer, for displaying to Ops users */
  customerName?: string;
  /** This property is obsolete and no longer in use. Defaults to Delivery. */
  deliveryChannel?: string;
  /** Represents a logical picking “deadline”, i.e., the time when all picking should be complete */
  pickingEndTime?: string;
  /** If present, picking will only begin after this time. Must be before the Picking End Time (PET). */
  earliestPickingStartTime?: string | null;
  /** When the customer should receive the order. Note that the time zone offsets (-04:00 in this example) must match the FC time zone offset, including across Daylight Saving Time shifts. This field is deprecated. Please use PickingEndTime. */
  orderWindow?: string[];
  /** If the customer has enabled or disabled substitutions on this order. Acceptable values include: ForbidSubstitutes */
  substitutionPreference?: string;
  /** Text field the customer entered with any notes */
  specialRequest?: string | null;
  /** The status of the order. One of Received, Batched, Stalled, Unstalled, Canceled, or Picked. */
  status?: string;
  /** List of line items */
  lineItems?: OrderLine[];
};
export type OrderLineSub = {
  orderLineSubstitutionIds?: string[];
  productId?: string;
  sku?: string;
  quantity?: Quantity;
};
export type OrderLineProperties = {
  orderLineIds?: string[];
  referenceIds?: string[];
  productId?: string;
  sku?: string;
  quantity?: {
    unit: string;
    value: number;
  };
  acceptableSubstitutions?: OrderLineSub[];
};
export type OrderProperties = {
  orderId?: string;
  referenceId?: string;
  priority?: string;
  pickingEndTime?: string;
  earliestPickingStartTime?: string | null;
  orderType?: string;
  orderLines?: OrderLineProperties[];
};
export type CartonContent = {
  productId?: string;
  sku?: string;
  quantity?: {
    unit: string;
    value: number;
  };
  /** The actual weights captured for variable weight SKUs. */
  actualWeights?: Quantity[] | null;
};
export type Carton = {
  toteId?: string;
  referenceId?: string;
  toteNumber?: number;
  contents?: CartonContent[];
};
export type TemperatureZone =
  | ("Ambient" | "Chilled" | "Frozen" | "ambient" | "chilled" | "frozen")
  | null;
export type AutostoreBin = {
  /** The AutoStore grid ID. */
  gridId?: string;
  /** The AutoStore bin number. */
  binNumber?: number;
  /** The AutoStore bin compartment number. */
  compartmentNumber?: number;
  /** The number of compartments in bin */
  numberOfCompartments?: number;
} | null;
export type WarehouseBin = {
  /** The aisle the bin is in. */
  aisle?: string | null;
  /** The shelf bay the bin is in. */
  shelfBay?: string | null;
  /** The pallet bay the bin is in. */
  palletBay?: string | null;
  /** The shelf the bin is on. */
  shelf?: string | null;
  /** The position of the bin. */
  position?: string | null;
} | null;
export type Bin = {
  /** The AutoStore ID for the bin containing the inventory */
  binId?: string;
  /** The bin type the inventory is contained in. If Autostore then the autostoreBin property will be populated, otherwise the warehouseBin will be populated. */
  binType?: string;
  temperatureZone?: TemperatureZone;
  autostoreBin?: AutostoreBin;
  warehouseBin?: WarehouseBin;
};
export type Pick = {
  pickId?: string;
  orderLineId?: string;
  productId?: string;
  sku?: string;
  quantity?: {
    unit: string;
    value: number;
  };
  /** This property is obsolete. Use Bin.BinId instead. */
  binId?: string;
  /** This property is obsolete. Use Bin.AutostoreBin.GridId instead. */
  autostoreGridId?: string | null;
  /** This property is obsolete. Use Bin.AutostoreBin.BinNumber instead. */
  autostoreBinNumber?: number | null;
  /** This property is obsolete. Use Bin.AutostoreBin.CompartmentNumber instead. */
  autostoreCompartmentNumber?: number | null;
  toteId?: string | null;
  batch?: string;
  status?: string;
  pickedBy?: string;
  bin?: Bin;
  /** Array of weights captured for variable weight (catch weight) picks. */
  weightsCaptured?: Quantity[] | null;
};
export type OrderFulfillmentState = {
  order?: OrderProperties;
  cartons?: Carton[];
  pickingPlan?: Pick[];
};
export type EventFeedItemMetadata = {
  /** A unique identifier of a user or an automated process that triggered the event */
  userId?: string | null;
};
export type Event = {
  /** A unique identifier of an aggregate the event belongs to. For example PickId, PutAwayTaskId, etc */
  _aggregateId: string;
  /** Sequentially incremented number representing casual order of events. It is used to fetch next portion/page of events to process from the feed */
  _offset: number;
  date_published: string;
  /** The event type in the format: aggregateName.eventName */
  type: string;
  id: string;
  url?: string;
  _metadata?: EventFeedItemMetadata;
};
export type OrderEventBase = Event & object;
export type OrderLocked = {
  type: "Order.Locked";
} & OrderEventBase & {
    _data?: {
      /** The client Order identifier */
      externalOrderId?: string | null;
    };
  };
export type OrderCanceled = {
  type: "Order.Canceled";
} & OrderEventBase & {
    _data?: {
      /** The client Order identifier */
      externalOrderId?: string | null;
    };
  };
export type OrderRepickRequested = {
  type: "Order.RepickRequested";
} & OrderEventBase & {
    _data?: {
      /** The client Order identifier. */
      externalOrderId?: string | null;
      /** The workstation where the repick was requested. */
      workstationId?: string | null;
      /** The workstation name where the repick was requested. */
      deviceId?: string | null;
      /** The tote for which the repick will be picked. */
      toteId?: string;
      /** The client ID for the tote for which the repick will be picked. */
      externalToteId?: string | null;
      /** The quantities that were requested to be repicked. */
      repickedQuantities?:
        | {
            /** The client order line item identifier */
            externalLineItemId?: string | null;
            /** The SKU that is requested to be repicked. */
            sku?: string;
            /** The quantity of the SKU that is requested to be repicked. */
            quantity?: Quantity;
          }[]
        | null;
      /** Why was the repick requested */
      reason?: string | null;
    };
  };
export type OrderLineItemCut = {
  type: "Order.LineItemCut";
} & OrderEventBase & {
    _data?: {
      /** The client Order identifier. */
      externalOrderId?: string | null;
      /** The client Order Line Item identifier. */
      externalLineItemId?: string | null;
      /** The SKU requested. */
      requestedSku?: string | null;
      /** The quantity originally requested. */
      requestedQuantity?: Quantity;
      /** The quantity cut by this operation. Does not include any past cuts. */
      cutQuantity?: Quantity;
      workstationId?: string | null;
      deviceId?: string | null;
    };
  };
export type OrderUnstalled = {
  type: "Order.Unstalled";
} & OrderEventBase & {
    _data?: {
      /** The client Order identifier */
      externalOrderId?: string | null;
    };
  };
export type OrderLoaded = {
  type: "Order.Loaded";
} & OrderEventBase & {
    _data?: {
      /** The client Order identifier */
      externalOrderId?: string | null;
    };
  };
export type OrderStalled = {
  type: "Order.Stalled";
} & OrderEventBase & {
    _data?: {
      /** The client Order identifier */
      externalOrderId?: string | null;
    };
  };
export type OrderStaged = {
  type: "Order.Staged";
} & OrderEventBase & {
    _data?: {
      /** The client Order identifier */
      externalOrderId?: string | null;
    };
  };
export type OrderReceived = {
  type: "Order.Received";
} & OrderEventBase & {
    _data?: {
      lineItems?:
        | {
            sku?: string;
            upcOrPlu?: string;
            externalLineItemId?: string | null;
            quantity?: Quantity;
            productName?: string | null;
            instructions?: string | null;
            acceptableSubstitutions?:
              | {
                  sku?: string;
                  quantity?: Quantity;
                }[]
              | null;
          }[]
        | null;
      priority?: ("Standard" | "Express") | null;
      /** Indicates the delivery method for the order (Pickup or Delivery) */
      deliveryChannel?: ("Pickup" | "Delivery") | null;
      earliestPickingStartTime?: string | null;
      /** This field is deprecated and will be removed in a future release. Please use PickingEndTime */
      orderWindow?: string[] | null;
      /** The date and time the order was created */
      createdDate?: string;
      /** The name of the customer the order is for */
      customerName?: string | null;
      /** The retailer supplied order ID */
      externalOrderId?: string | null;
      specialRequest?: string | null;
      pickingEndTime?: string;
      substitutionPreference?:
        | ("SubstitutionNotProvided" | "ForbidSubstitutes")
        | null;
      /** Indicates the type of order (Fresh or Shop) */
      orderType?: ("Fresh" | "Shop") | null;
    };
  };
export type OrderBatched = {
  type: "Order.Batched";
} & OrderEventBase & {
    _data?: {
      /** The client Order identifier */
      externalOrderId?: string | null;
    };
  };
export type OrderVerified = {
  type: "Order.Verified";
} & OrderEventBase & {
    _data?: {
      /** The client Order identifier */
      externalOrderId?: string | null;
    };
  };
export type OrderPicked = {
  type: "Order.Picked";
} & OrderEventBase & {
    _data?: {
      /** The client Order identifier */
      externalOrderId?: string | null;
      /** Collection of the Order picks */
      picks?:
        | {
            /** SKU of the picked product */
            sku?: string;
            /** Unique identifier of the Line Item of the picked product in the order, set by the client. */
            externalLineItemId?: string | null;
            /** UPC of the picked product */
            upcOrPlu?: string;
            /** Unique identifier of the Line Item of the picked product in the order, set by AutoStore. */
            lineItemId?: string;
            /** DEPRECATED. Not in use. */
            exception?: {
              type?: string | null;
            } | null;
            status?: "Completed" | "Canceled" | "Scheduled";
            /** Unique identifier of the Pick. */
            id?: string;
            /** Quantity picked */
            quantity?: Quantity;
            /** The user who completed the pick */
            userId?: string | null;
            /** The workstation where the pick was completed */
            workstationId?: string | null;
            /** DEPRECATED. PLEASE USE expirationDate INSTEAD. The expiration date of the Line Item of the picked product, if known */
            expiration?: string | null;
            /** The expiration date of the Line Item of the picked product, if known */
            expirationDate?: string | null;
            /** The bin location of the picked product */
            binLocation?: {
              binNumber?: number | null;
              autostoreCompartmentNumber?: number | null;
            } | null;
            deviceId?: string | null;
          }[]
        | null;
    };
  };
export type OrderEvent =
  | OrderLocked
  | OrderCanceled
  | OrderRepickRequested
  | OrderLineItemCut
  | OrderUnstalled
  | OrderLoaded
  | OrderStalled
  | OrderStaged
  | OrderReceived
  | OrderBatched
  | OrderVerified
  | OrderPicked;
export const {
  usePostOrdersCreateMutation,
  useGetOrdersByOrderIdQuery,
  usePostOrdersByOrderIdCancelMutation,
  useGetOrdersByOrderIdFulfillmentStatusQuery,
  useGetOrdersByAggregateIdEventsQuery,
  useGetOrdersEventsQuery
} = injectedRtkApi;
